import { SettingOutlined } from '@ant-design/icons';
import type {
  MenuDataItem,
  Settings as ProLayoutSettings,
} from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, setLocale } from '@umijs/max';
import {
  type LayoutSettings as CoreLayoutSettings,
  type CoreSessionConfig,
  type CurrentUser,
  SessionProvider,
  useSession,
  useShell,
  useTheme,
} from '@zhiyue/core';
import { AiChat } from '@zhiyue/xiaoyueyue';
import { Button } from 'antd';
import { type BusinessType, platformApi } from '@/platform/api';
import SearchBox from '@/platform/SearchBox';
import {
  accessibleLayoutMenu,
  businessHeaderMenu,
  nestPostInvestmentMenu,
  settingsSiderMenu,
} from '@/platform/settingsNavigation';
import SessionBoundary from '@/platform/wrappers/SessionBoundary';
import LayoutSettingsSync from './platform/LayoutSettingsSync';
import {
  menuTarget,
  OVERVIEW_ROUTES,
  overviewGroup,
} from './platform/overviewNavigation';
import PlatformThemeBoundary from './platform/ThemeBoundary';
import '@zhiyue/core/style.css';
import '@zhiyue/xiaoyueyue/style.css';
import '@zhiyue/schedule/style.css';
import '@zhiyue/task-management/style.css';

import { App as AntdApp } from 'antd';
import { BizAppConfig } from 'config/application.manifest';
import { getBrandAppName, getStoredBrandLocale } from 'config/brand';
import React from 'react';
import { sessionAdapter } from '@/adapters/sessionAdapter';
import { AvatarDropdown, NotificationIcon, ThemeToggle } from '@/components';
import SiderResizeHandle, {
  DEFAULT_SIDER_WIDTH,
  getStoredSiderWidth,
} from '@/components/Layout/SiderResizeHandle';
import { WorkbenchProvider } from '@/features/workbench/model';
import type {
  AuthorizedResourceAccessMap,
} from '@/permissions/adapter';
import {
  clearAutoRefresh,
  consumeApplicationAccessReturnTo,
  ensureValidAccessToken,
  getAccessToken,
  hasOAuth2Session,
  redirectToSSO,
  setupAutoRefresh,
} from '@/services/oauth2';
import {
  getLayoutVisibility,
  updateSessionSnapshot,
} from '@/state/sessionSnapshot';
import defaultSettings, { getNavigationToken } from '../config/defaultSettings';
import { readCurrentTenantUuid } from './adapters/tenantStorage';
import { errorConfig } from './requestErrorConfig';
import { hasRegisteredScenes } from './services/bff/llm-scene.api';

export const request: RequestConfig = {
  ...errorConfig,
};

const loginPath = '/user/login';
const publicPaths = [loginPath, '/callback'];

const getSettingsWithSiderWidth = (locale?: string) => ({
  ...defaultSettings,
  title: getBrandAppName(locale),
  siderWidth: getStoredSiderWidth(),
});

const getSessionConfig = (): CoreSessionConfig => {
  // Core owns subsequent manual theme changes. Seed its existing preference
  // only when no valid choice exists; never derive the default from the OS.
  if (typeof window !== 'undefined') {
    const key = 'app_nav_theme';
    const stored = window.localStorage.getItem(key);
    const preference =
      stored === 'light' || stored === 'realDark' ? stored : 'realDark';
    if (stored !== preference) window.localStorage.setItem(key, preference);
    document.documentElement.dataset.theme =
      preference === 'realDark' ? 'dark' : 'light';
  }
  return {
    redirectAfterLogout: '/user/login',
    adapter: sessionAdapter,
    ui: {
      links: {
        help: 'https://docs.shanhuyun.cn/',
      },
      userMenu: {
        items: [],
      },
      locales: [
        { key: 'en-US', label: 'English', icon: '🇺🇸', title: 'Language' },
        { key: 'ja-JP', label: '日本語', icon: '🇯🇵', title: '言語' },
        { key: 'zh-CN', label: '简体中文', icon: '🇨🇳', title: '语言' },
        { key: 'zh-TW', label: '繁體中文', icon: '🇭🇰', title: '語言' },
      ],
    },
    featureFlags: {
      showHeader: true,
      showNav: true,
      showFooter: true,
      enableTenantSwitch: true,
      enableRegister: false,
    },
    onTenantSwitched: () => {
      const applicationReturnTo = consumeApplicationAccessReturnTo();
      if (applicationReturnTo) {
        window.location.href = applicationReturnTo;
        return;
      }

      window.location.href = '/';
    },
    onLocaleChanged: (locale) => {
      setLocale(locale, false);
    },
    onUnauthorized: () => {
      if (!publicPaths.includes(history.location.pathname)) {
        redirectToSSO();
      }
    },
  };
};

const SessionSync: React.FC = () => {
  const { navTheme } = useTheme();
  const { currentUser, loading } = useSession();
  const { shellConfig } = useShell();
  const themeSyncedRef = React.useRef<CoreLayoutSettings['navTheme'] | null>(
    null,
  );

  React.useEffect(() => {
    updateSessionSnapshot({
      currentUser,
      loading,
      featureFlags: shellConfig.featureFlags,
    });
  }, [currentUser, loading, shellConfig.featureFlags]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (currentUser && hasOAuth2Session()) {
      setupAutoRefresh();
      return;
    }

    clearAutoRefresh();
  }, [currentUser, loading]);

  React.useEffect(() => {
    if (themeSyncedRef.current === navTheme) {
      return;
    }
    themeSyncedRef.current = navTheme;

    const useDarkMode = navTheme === 'realDark';

    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = useDarkMode ? 'dark' : 'light';
    }
  }, [navTheme]);

  return null;
};

export const rootContainer = (
  container: React.ReactNode,
  // umi 只把 { routes, plugin, history } 传给 rootContainer，这里拿不到
  // initialState / setInitialState；依赖它们的同步放在 layout 渲染内
  // （见 LayoutSettingsSync）。下方 initialState 为历史兜底，运行时为 undefined。
  { initialState }: { initialState: any },
) => {
  return (
    <RootSessionContainer>
      <SessionBoundary>
        <WorkbenchProvider
          initialRole={
            initialState?.enabledBusinessTypes?.includes('state')
              ? 'partner'
              : initialState?.enabledBusinessTypes?.includes('pevc')
                ? 'investmentManager'
                : 'research'
          }
        >
          {container}
        </WorkbenchProvider>
      </SessionBoundary>
    </RootSessionContainer>
  );
};

const RootSessionContainer: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const sessionConfig = React.useMemo(() => getSessionConfig(), []);

  return (
    <SessionProvider config={sessionConfig}>
      <AntdApp>
        <SessionSync />
        {children}
        <FloatingAiChat />
      </AntdApp>
    </SessionProvider>
  );
};

export const innerProvider = (container: React.ReactNode) => (
  <PlatformThemeBoundary>{container}</PlatformThemeBoundary>
);

const FloatingAiChat: React.FC = () => {
  const { currentUser, loading } = useSession();
  const { navTheme } = useTheme();

  if (
    loading ||
    !currentUser ||
    publicPaths.includes(history.location.pathname)
  ) {
    return null;
  }

  return (
    <AiChat
      title="AI 助手"
      position="bottom-right"
      theme={navTheme === 'realDark' ? 'dark' : 'light'}
      authHeaders={() => {
        const headers: Record<string, string> = {};
        const token = getAccessToken();
        const tenantUuid = readCurrentTenantUuid() || currentUser.tenantUuid;
        const organizationUuid =
          (
            currentUser as CurrentUser & {
              organizationUuid?: string;
              lastLoginOrganizationUuid?: string;
            }
          ).organizationUuid ||
          (
            currentUser as CurrentUser & {
              lastLoginOrganizationUuid?: string;
            }
          ).lastLoginOrganizationUuid;

        if (token) headers.Authorization = `Bearer ${token}`;
        if (tenantUuid) headers['x-tenant-id'] = tenantUuid;
        if (organizationUuid) headers['x-organization-id'] = organizationUuid;
        return headers;
      }}
    />
  );
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<ProLayoutSettings & CoreLayoutSettings>;
  currentUser?: API.CurrentUser & CurrentUser;
  authorizedResourceAccess?: AuthorizedResourceAccessMap;
  currentTenantIsAdmin?: boolean;
  hasRegisteredAiScenes?: boolean;
  loading?: boolean;
  enabledBusinessTypes?: BusinessType[];
}> {
  let authorizedResourceAccess: AuthorizedResourceAccessMap = {};
  let currentTenantIsAdmin = false;
  let enabledBusinessTypes: BusinessType[] = [];
  let hasRegisteredAiScenes = false;

  // callback 页面里会在 token 落库后主动 refresh 初始态；
  // 这里不能再排除 /callback，否则首登回跳后权限态会一直是空的。
  if (hasOAuth2Session()) {
    const [authorizedResourcesResult, platformConfigResult] =
      await Promise.allSettled([platformApi.access(), platformApi.config()]);
    if (authorizedResourcesResult.status === 'fulfilled') {
      authorizedResourceAccess =
        authorizedResourcesResult.value.authorizedResourceAccess;
      currentTenantIsAdmin =
        authorizedResourcesResult.value.currentTenantIsAdmin;
    }

    if (platformConfigResult.status === 'fulfilled') {
      enabledBusinessTypes = platformConfigResult.value.enabledBusinessTypes;
    }
    if (currentTenantIsAdmin) {
      hasRegisteredAiScenes = await hasRegisteredScenes(
        BizAppConfig.appSsoClientId || '',
      );
    }
  }
  return {
    loading: true,
    authorizedResourceAccess,
    currentTenantIsAdmin,
    enabledBusinessTypes,
    hasRegisteredAiScenes,
    settings: getSettingsWithSiderWidth(getStoredBrandLocale()) as Partial<
      ProLayoutSettings & CoreLayoutSettings
    >,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const getCurrentLayoutVisibility = () =>
    getLayoutVisibility(history.location.pathname);
  const siderWidth =
    (
      initialState?.settings as Partial<CoreLayoutSettings> & {
        siderWidth?: number;
      }
    )?.siderWidth ?? DEFAULT_SIDER_WIDTH;

  const resizeEnabled =
    typeof window !== 'undefined' &&
    window.innerWidth > 768 &&
    initialState?.settings?.layout !== 'top' &&
    !(initialState?.settings as { collapsed?: boolean } | undefined)?.collapsed;

  const handleResizeWidth = React.useCallback(
    (nextWidth: number) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings =
          (prev.settings as Partial<CoreLayoutSettings> & {
            siderWidth?: number;
          }) || {};
        if (prevSettings.siderWidth === nextWidth) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            siderWidth: nextWidth,
          },
        };
      });
    },
    [setInitialState],
  );

  const handleCollapse = React.useCallback(
    (collapsed: boolean) => {
      setInitialState((prev) => {
        if (!prev) return prev;
        const prevSettings = prev.settings || {};
        if (prevSettings.collapsed === collapsed) {
          return prev;
        }
        return {
          ...prev,
          settings: {
            ...prevSettings,
            collapsed,
          },
        };
      });
    },
    [setInitialState],
  );

  return {
    ...initialState?.settings,
    layout: 'mix',
    splitMenus: true,
    // 全部项目与父分组共用 key；我的项目仅选中所属模块和自身。
    selectedKeys:
      history.location.pathname.replace(/\/$/, '') ===
      '/portfolio/portfolio/mine'
        ? ['/portfolio', '/portfolio/portfolio/mine']
        : undefined,
    token: getNavigationToken(initialState?.settings?.navTheme === 'realDark'),
    fixedHeader: true,
    fixSiderbar: true,
    contentStyle: {
      height: 'calc(100vh - 44px)',
      overflow: 'auto',
      minWidth: 0,
    },
    breadcrumbRender: false,
    actionsRender: (props) => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) {
        return [];
      }

      if (props.layout === 'side' && !props.isMobile) {
        return [
          <SearchBox key="search" />,
          ...(initialState?.currentTenantIsAdmin
            ? [
                <Button
                  type="text"
                  key="platform-settings"
                  aria-label="平台配置"
                  icon={<SettingOutlined />}
                  onClick={() => history.push('/settings/platform')}
                />,
              ]
            : []),
          <NotificationIcon key="sider-notification" />,
          <ThemeToggle key="sider-theme-toggle" />,
        ];
      }

      return [
        <SearchBox key="search" />,
        ...(initialState?.currentTenantIsAdmin
          ? [
              <Button
                type="text"
                key="platform-settings"
                aria-label="平台配置"
                icon={<SettingOutlined />}
                onClick={() => history.push('/settings/platform')}
              />,
            ]
          : []),
        <NotificationIcon key="notification" />,
        <ThemeToggle key="themeToggle" />,
      ];
    },
    avatarProps: {
      render: () => {
        const { showHeader } = getCurrentLayoutVisibility();
        if (!showHeader) {
          return null;
        }
        return <AvatarDropdown />;
      },
    },
    waterMarkProps: {
      content: '',
    },
    footerRender: () => {
      const { showFooter } = getCurrentLayoutVisibility();
      if (!showFooter) {
        return false;
      }
      return null;
    },
    menuDataRender: (items) =>
      nestPostInvestmentMenu(accessibleLayoutMenu(items)),
    headerRender: (props, dom) => {
      const { showHeader } = getCurrentLayoutVisibility();
      if (!showHeader) return false;
      return React.isValidElement(dom)
        ? React.cloneElement(
            dom as React.ReactElement<{ menuData: MenuDataItem[] }>,
            {
              menuData: businessHeaderMenu(props.menuData),
            },
          )
        : dom;
    },
    menuRender: (props, dom) => {
      const { showNav } = getCurrentLayoutVisibility();
      if (!showNav) return false;
      const settingsMenu = settingsSiderMenu(
        history.location.pathname,
        props.menuData,
      );
      return settingsMenu && React.isValidElement(dom)
        ? React.cloneElement(
            dom as React.ReactElement<{ menuData: MenuDataItem[] }>,
            {
              menuData: settingsMenu,
            },
          )
        : dom;
    },
    headerTitleRender: undefined,
    menuItemRender: (item, dom) => {
      const target = menuTarget(item);
      return target ? (
        <a
          href={target}
          onClick={(event) => {
            event.preventDefault();
            history.push(target);
          }}
        >
          {dom}
        </a>
      ) : (
        dom
      );
    },
    subMenuItemRender: (item, dom) => {
      // 投后管理是展示层分组，没有独立页面；点击只展开/收起。
      const presentationalGroup = item.key === '/portfolio/post-investment';
      // 分组概览可能以 hideInMenu 隐藏（如退出管理），标题仍应直接进入概览。
      const target =
        !presentationalGroup && overviewGroup(item.path)
          ? (OVERVIEW_ROUTES[item.path ?? ''] ?? menuTarget(item))
          : undefined;
      return target ? (
        <a
          href={target}
          onClick={(event) => {
            event.preventDefault();
            history.push(target);
          }}
        >
          {dom}
        </a>
      ) : (
        dom
      );
    },
    onPageChange: () => {
      const { pathname } = history.location;
      if (publicPaths.includes(pathname)) {
        return;
      }

      void ensureValidAccessToken();
    },
    menuHeaderRender: undefined,
    onCollapse: handleCollapse,
    siderWidth,
    childrenRender: (children) => {
      const { showNav } = getCurrentLayoutVisibility();
      return (
        <>
          <LayoutSettingsSync
            setInitialState={setInitialState}
            currentNavTheme={initialState?.settings?.navTheme}
            currentTitle={initialState?.settings?.title}
            currentUser={initialState?.currentUser}
          />
          <PlatformThemeBoundary>{children}</PlatformThemeBoundary>
          <SiderResizeHandle
            enabled={showNav && resizeEnabled}
            siderWidth={siderWidth}
            onResizeWidth={handleResizeWidth}
          />
          {/*
            这里原先在 dev 下挂 ProLayout 的 SettingDrawer（脚手架自带的可视化配置小工具）。
            它会把一个 position:fixed 的齿轮方块钉在正文右缘正中，压住重试按钮与表格「操作」列，
            且 pro-layout 7.22.7 的 SettingDrawerProps 没有隐藏该手柄的 prop。
            本应用主题/布局切换走顶栏「切换为暗色主题」按钮与 LayoutSettingsSync，该工具属冗余，故不再渲染。
          */}
        </>
      );
    },
  };
};
